import React, { FC, useEffect } from 'react';

import {
  Box,
  Flex,
  Modal,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import { Form, Formik, useFormikContext } from 'formik';

import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';

import { selectors } from 'store/slices/washhub';

import { getInitialValues, validationSchema } from './EditItemModal.utils';

import { SKU } from 'types/sku';
import { Status } from 'types/items';
import { EditItemModalViewProps } from './EditItemModal.props';
import { useSites } from 'hooks/sites/useSites';
import { Site } from 'types/site';
import { SiteFilterableSelect } from '../SiteFilterableSelect';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const EditItemModalView: FC<EditItemModalViewProps> = (props) => {
  const {
    skus,
    item,
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    isLoadingSKU,
  } = props;

  const washHub = selectors.useSelectedWashHub();
  const { data = [] } = useSites();
  const sites = data;

  return (
    <Modal
      size="6xl"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader textAlign="center" fontWeight="500">
          Update item
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" px={6} pb={6}>
            <Formik
              onSubmit={onSubmit}
              initialValues={getInitialValues(item)}
              validationSchema={validationSchema}>
              {({ values }) => (
                <Form>
                  <Flex>
                    <FormikSelectInput
                      placeholder="Select"
                      label="SKU (*)"
                      isLoading={isLoadingSKU}
                      containerProps={{ flex: 1 }}
                      name="sku"
                      labelProps={labelProps}>
                      {skus?.map((sku) => (
                        <option key={sku._id} value={sku._id}>
                          {sku?.name || ''}
                        </option>
                      ))}
                    </FormikSelectInput>
                    <Box h="20px" w="20px" />
                    <FormikInput
                      name="washHub"
                      label="Wash Hub"
                      labelProps={labelProps}
                      isReadOnly
                      value={washHub?.name || ''}
                      containerProps={{ flex: 1 }}
                    />
                    <Box h="20px" w="20px" />
                    <FormikInput
                      name="sku"
                      label="SKU ID (*)"
                      isReadOnly
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                      bg="#f1f1f1"
                      value={values?.sku}
                    />
                  </Flex>
                  <Flex mt={4}>
                    <SkuSize skus={skus} />
                    <Box h="20px" w="20px" />
                    <SkuType skus={skus} />
                    <Box h="20px" w="20px" />
                    <FormikInput
                      name="rfid"
                      label="RFID"
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                      bg="#f1f1f1"
                    />
                  </Flex>
                  <Flex mt={4}>
                    <FormikSelectInput
                      placeholder="Select"
                      label="Status"
                      containerProps={{ flex: 1 }}
                      name="status"
                      maxW="250px"
                      labelProps={labelProps}>
                      {Object.values(Status)
                        .filter((status) => typeof status === 'string')
                        .map((status, index) => (
                          <option key={status} value={index}>
                            {status || ''}
                          </option>
                        ))}
                    </FormikSelectInput>
                    <Box h="20px" w="10px" />
                    {Status[values?.status] === 'PACKED' ? (
                      <FormikInput
                        name="packageId"
                        label="Package ID"
                        labelProps={labelProps}
                        containerProps={{ flex: 2 }}
                        value={item?.currentPackage}
                      />
                    ) : (
                      <></>
                    )}
                    <SiteFilterableSelect
                      containerProps={{ flex: 2 }}
                      label="Venue"
                      name="venue"
                      labelProps={labelProps}
                      isRequired={false}
                      elementContainerProps={{ maxW: '250px' }}
                    />
                  </Flex>
                  <Flex mt={4}>
                    <FormikInput
                      name="qrCode"
                      label="Unique QR Code"
                      labelProps={labelProps}
                      containerProps={{ flex: 1, maxW: '550px' }}
                      placeholder="https://qr.rworldreuse.com/..."
                    />
                  </Flex>
                  <Flex my={12} flexDirection="row" justifyContent="center">
                    <Button
                      variant="outline"
                      w="134px"
                      marginRight="10px"
                      onClick={onClose}
                      isDisabled={isLoading}>
                      Cancel
                    </Button>
                    <Button type="submit" w="134px" isLoading={isLoading}>
                      Update
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditItemModalView;

type Props = { skus: SKU[] };

const SkuSize: FC<Props> = ({ skus }) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const skuId = values?.sku;

  useEffect(() => {
    const _sku = skus?.find((s) => s._id === skuId);

    setFieldValue('size', _sku?.size || '');
  }, [skuId]);

  return (
    <FormikInput
      name="size"
      label="Size"
      isReadOnly
      labelProps={labelProps}
      containerProps={{ flex: 1 }}
      bg="#f1f1f1"
    />
  );
};

const SkuType: FC<Props> = ({ skus }) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const skuId = values?.sku;

  useEffect(() => {
    const _sku = skus?.find((s) => s._id === skuId);

    setFieldValue('type', _sku?.type || '');
  }, [skuId]);

  return (
    <FormikInput
      name="type"
      label="Items Type"
      isReadOnly
      labelProps={labelProps}
      containerProps={{ flex: 1 }}
      bg="#f1f1f1"
    />
  );
};
